import * as Sentry from '@sentry/react';
import { safeStorage } from './safeStorage';

interface RecoverableHydrationErrorInfo {
  componentStack?: string;
}

function serializeCause(cause: unknown): string | undefined {
  if (typeof cause === 'undefined') {
    return undefined;
  }

  if (cause instanceof Error) {
    return `${cause.name}: ${cause.message}`;
  }

  try {
    return JSON.stringify(cause);
  } catch {
    return String(cause);
  }
}

export function reportRecoverableHydrationError(
  error: unknown,
  errorInfo: RecoverableHydrationErrorInfo
): void {
  const root = document.getElementById('root');
  const errorName = error instanceof Error ? error.name : typeof error;
  const errorMessage = error instanceof Error ? error.message : String(error);
  const errorStack = error instanceof Error ? error.stack : undefined;
  const cause = error instanceof Error && 'cause' in error
    ? serializeCause((error as Error & { cause?: unknown }).cause)
    : undefined;

  const context = {
    href: window.location.href,
    pathname: window.location.pathname,
    search: window.location.search,
    locale: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    userAgent: navigator.userAgent,
    hasToken: safeStorage.getItem('access_token') !== null,
    rootChildCount: root?.childNodes.length ?? 0,
    rootHtmlSnippet: root?.innerHTML.slice(0, 1500) ?? '',
    componentStack: errorInfo.componentStack ?? '',
    errorName,
    errorMessage,
    errorStackSnippet: errorStack?.slice(0, 2000) ?? '',
    cause: cause ?? '',
  };

  if (import.meta.env.DEV) {
    console.error('Recoverable hydration error', error, context);
  }

  if (!import.meta.env.PROD || !import.meta.env.VITE_SENTRY_DSN) {
    return;
  }

  // Surface the error message as a breadcrumb so it's attached to the session
  // replay timeline even if Sentry's backend groups the event into the synthetic
  // replay_hydration_error issue (which hides the underlying exception message).
  Sentry.addBreadcrumb({
    category: 'react-hydration',
    level: 'warning',
    message: errorMessage,
    data: { name: errorName, pathname: window.location.pathname },
  });

  Sentry.withScope((scope) => {
    scope.setLevel('warning');
    scope.setTag('error_type', 'react_hydration');
    scope.setTag('hydration_error_name', errorName);
    scope.setTag('hydration_error_preview', errorMessage.slice(0, 200));
    scope.setFingerprint(['react-hydration', window.location.pathname]);
    scope.setContext('hydration', context);

    if (errorInfo.componentStack) {
      scope.setExtra('componentStack', errorInfo.componentStack);
    }

    Sentry.captureException(
      error instanceof Error ? error : new Error('Recoverable hydration error')
    );
  });
}
